import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from './icons';
import { useDebouncedValue } from './useDebouncedValue';
import { fetchHelpSearch, HelpSearchHit } from './helpdeskApi';

interface LiveSearchBoxProps {
    /** Applies the `.search--page` modifier used on the dedicated search page. */
    variant?: 'hero' | 'page';
    initialValue?: string;
    lang?: string;
}

/**
 * Search input with a live-results dropdown, re-implemented from the
 * prototype's public/client.js `[data-live-search]` behavior:
 * debounced fetch (140ms), arrow-key navigation, Enter-to-navigate, and
 * click-outside / blur to close. Submitting the form navigates to
 * /helpdesk/search?q=... exactly like the prototype's GET form action.
 */
const LiveSearchBox: React.FC<LiveSearchBoxProps> = ({ variant = 'hero', initialValue = '', lang = 'en' }) => {
    const navigate = useNavigate();
    const [value, setValue] = useState(initialValue);
    const [results, setResults] = useState<HelpSearchHit[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [hasSearched, setHasSearched] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const debouncedValue = useDebouncedValue(value, 140);

    useEffect(() => {
        const term = debouncedValue.trim();
        if (term.length < 2) {
            setResults([]);
            setHasSearched(false);
            setIsOpen(false);
            return;
        }
        let cancelled = false;
        fetchHelpSearch(term, lang, 6).then((hits) => {
            if (cancelled) return;
            setResults(hits);
            setHasSearched(true);
            setIsOpen(true);
            setActiveIndex(-1);
        });
        return () => {
            cancelled = true;
        };
    }, [debouncedValue, lang]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (formRef.current && !formRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setIsOpen(false);
        navigate(`/helpdesk/search?q=${encodeURIComponent(value.trim())}`);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || results.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((prev) => (prev + 1) % results.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
        } else if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            navigate(results[activeIndex].url);
        } else if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <form
            ref={formRef}
            className={`hc-search${variant === 'page' ? ' hc-search--page' : ''}`}
            role="search"
            onSubmit={handleSubmit}
        >
            <SearchIcon className="hc-search__icon" size={20} />
            <input
                type="search"
                name="q"
                id="hc-q"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    if (value.trim().length >= 2 && results.length) setIsOpen(true);
                }}
                placeholder={
                    variant === 'page'
                        ? 'Search the help center…'
                        : 'Search "traffic not showing", "payment failed", "pause campaign"…'
                }
                autoComplete="off"
                aria-label="Search the help center"
            />
            <button type="submit" className="hc-btn hc-btn--primary">
                Search
            </button>
            {isOpen && (
                <div className="hc-search__results" role="listbox" aria-label="Search suggestions">
                    {results.length === 0 && hasSearched ? (
                        <div className="hc-sr-empty">No matches for &ldquo;{value.trim()}&rdquo;. Press Enter to see suggestions.</div>
                    ) : (
                        results.map((r, idx) => (
                            <a
                                key={r.url}
                                href={r.url}
                                role="option"
                                aria-selected={idx === activeIndex}
                                className={idx === activeIndex ? 'is-active' : undefined}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsOpen(false);
                                    navigate(r.url);
                                }}
                            >
                                <span className="hc-sr-cat">{r.categoryTitle}</span>
                                <span className="hc-sr-title">{r.title}</span>
                            </a>
                        ))
                    )}
                </div>
            )}
        </form>
    );
};

export default LiveSearchBox;
